import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Avatar,
  FormControl,
  Select,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Grid,
} from "@mui/material";
import {
  Search,
  Add,
  MoreVert,
  ArrowForward,
  NotificationsActive,
  Storage,
  Apps,
  People,
  Assessment,
  Settings,
  Edit,
  Delete,
  Archive,
  Unarchive,
  Language,
  PictureAsPdf,
  CheckCircle as CheckIcon,
  Cancel as CrossIcon,
  Info,
  SmartToy as SmartToyIcon,
  BugReport as BugReportIcon,
  FlashOn as FlashIcon,
  Image,
  Description,
  TableChart,
  Slideshow,
  FolderZip,
  Code,
  ChatBubbleOutline,
  LinkOff,
  Refresh,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Sync as SyncIcon,
  ContactSupport,
  Close as CloseIcon,
  EmailOutlined as EmailIcon,
  Chat as ChatIcon,
  Mic as MicIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import {
  USER_ROLES,
  API_CONFIG,
  API_ENDPOINTS,
  STORAGE_KEYS,
  SOCKET_KEYS,
  CHANNEL_TYPES_CONFIG,
  getChannelTypeById,
  DISABLE_SUBSCRIPTION,
} from "../../helpers/constants";
import { LanguageConstants } from "../../helpers/LanguageConstants";
import AddSpaceModal from "../../components/AddSpaceModal";
import ChannelSettings from "../../components/ChannelSettings";
import type { ChannelSettingsTabId } from "../../components/ChannelSettings/channelSettingsTabs";
import PlanDetails from "../../components/PlanDetails";
import WorkspaceTagAutocomplete from "../../components/WorkspaceTagAutocomplete";
import ChannelRestrictionModal from "../../components/ChannelRestrictionModal";
import { useToast } from "../../contexts/ToastContext";
import { useSubscriptionValidation } from "../../hooks/useSubscriptionValidation";
import socketService from "@/services/socketService";
import channelService from "../../services/channelService";
import { agentsService, Agent } from "../../services/agentsService";
import { disconnectEmailAccount, reconnectEmailAccount, deleteEmailData, getEmailSyncStatus } from "../../services/emailService";
import { capitalizeChannelName, capitalizeFirstLetter } from "../../helpers/stringUtils";
import { getFetchErrorMessage } from "../../helpers/errorUtils";
import { useChannelAdmin, prefetchChannelAdmin } from "../../hooks/useChannelAdmin";
import ChannelTypeChip from "../../components/ChannelTypeChip";

// Types for API response
interface Channel {
  id: string;
  name: string;
  description: string;
  channel_description?: string;
  workspace_id: string;
  workspace_name: string;
  company_id: string;
  is_active: boolean;
  is_public: boolean;
  ai_enabled: boolean;
  is_archived: boolean;
  channel_type?: 'email' | 'message' | 'voice';
  messaging_provider?: 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams';
  channel_settings: {
    auto_summarize: boolean;
    notification_preferences: {
      email: boolean;
      push: boolean;
    };
  };
  message_count: number;
  last_message_at: string | null;
  total_apps_connected: number;
  total_users: number;
  total_datasources: number;
  app: {
    app_name: string;
    icon_url: string;
    app_key?: string;
  }[];
  datasource: {
    datasource_name: string;
    file_name: string;
    file_type: string;
  }[];
  agent_count: number;
  agent_names: Record<string, any>;
  workflow_count: number;
  RCA: string;
  created_at: string;
  updated_at: string;
  provider?: string;
  /** List of category/tags for the channel (from channel API) */
  channel_tags?: string[];
  /** List of sub-category/subtags for the channel (from channel API; response key: channel_sub_tags) */
  channel_sub_tags?: string[];
  /** Plain-English automation rules for the channel */
  channel_business_rules?: string;
}

interface ChannelsResponse {
  channels: Channel[];
  total: number;
  page: number;
  size: number;
  /** When true and channels are empty, show "Show Archived Channels" and optionally auto-check it. */
  has_archived_channels?: boolean;
}



const GroupsPage: React.FC = () => {
  // State for filters and search
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedWorkspaceTag, setSelectedWorkspaceTag] = useState<string>("all");
  const [selectedChannelType, setSelectedChannelType] = useState<string>("all");
  const [showArchivedChannels, setShowArchivedChannels] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [openAddSpaceModal, setOpenAddSpaceModal] = useState<boolean>(false);

  // State for API data
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  /** From API: when no active channels but archived exist, show and auto-check "Show Archived Channels". */
  const [hasArchivedChannels, setHasArchivedChannels] = useState<boolean>(false);

  // State for delete confirmation dialog
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] =
    useState<boolean>(false);
  const [channelToDelete, setChannelToDelete] = useState<Channel | null>(null);
  const [isDeletingChannel, setIsDeletingChannel] = useState<boolean>(false);

  // State for archive confirmation dialog
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState<boolean>(false);
  const [channelToArchive, setChannelToArchive] = useState<Channel | null>(null);
  const [isArchivingChannel, setIsArchivingChannel] = useState<boolean>(false);
  const [isUnarchivingChannel, setIsUnarchivingChannel] = useState<boolean>(false);

  // State for disconnect confirmation dialog
  const [isDisconnectConfirmOpen, setIsDisconnectConfirmOpen] = useState<boolean>(false);
  const [channelToDisconnect, setChannelToDisconnect] = useState<Channel | null>(null);
  const [isDisconnectingChannel, setIsDisconnectingChannel] = useState<boolean>(false);
  
  // State for reconnect functionality
  const [isReconnectingChannel, setIsReconnectingChannel] = useState<boolean>(false);
  const [emailStatuses, setEmailStatuses] = useState<Record<string, { is_active: boolean }>>({});

  // State for email sync status
  const [syncStatus, setSyncStatus] = useState<Record<string, any>>({});

  // State for ChannelSettings component
  const [openChannelSettings, setOpenChannelSettings] =
    useState<boolean>(false);
  const [selectedChannelForSettings, setSelectedChannelForSettings] =
    useState<Channel | null>(null);
  /** Set only when ChannelSettings is opened via the "Edit" menu item (below) rather than
   * "Settings" — routes straight to the Channel Config tab (where the real inline edit + "See
   * workflow canvas" flow already lives for both email and message channels), or to the
   * Business Rules Workflow tab once that tab's own "See workflow canvas" button is clicked.
   * Reset to undefined on close so a later plain "Settings" click keeps its own default (Config)
   * tab. */
  const [channelSettingsInitialTabId, setChannelSettingsInitialTabId] =
    useState<ChannelSettingsTabId | undefined>(undefined);
  /** Paired with channelSettingsInitialTabId === 'config' — auto-opens that tab's own Edit
   * dialog (email's "Edit Channel" dialog, or message's EditSpaceModal) the moment the drawer
   * lands there, via ChannelSettings' initialOpenConfigEdit prop. Reset alongside the tab id. */
  const [channelSettingsInitialOpenConfigEdit, setChannelSettingsInitialOpenConfigEdit] =
    useState(false);

  // When the settings drawer opens, fetch the channel detail to supplement fields
  // (e.g. channel_business_rules) that the channels list API may not return.
  useEffect(() => {
    if (!openChannelSettings || !selectedChannelForSettings?.id) return;
    const channelId = selectedChannelForSettings.id;
    const fetchDetail = async () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        const res = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.GET_BY_ID(channelId)}`,
          {
            method: 'GET',
            headers: {
              accept: 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!res.ok) return;
        const data = await res.json();
        setSelectedChannelForSettings((prev) =>
          prev?.id === channelId
            ? { ...prev, channel_business_rules: data.channel_business_rules ?? prev.channel_business_rules }
            : prev
        );
      } catch {
        // silently ignore — list data is still used as fallback
      }
    };
    fetchDetail();
  }, [openChannelSettings, selectedChannelForSettings?.id]);

  // State for agents data
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState<boolean>(false);
  
  // State for agents modal
  const [openAgentsModal, setOpenAgentsModal] = useState<boolean>(false);
  const [selectedChannelAgents, setSelectedChannelAgents] = useState<Agent[]>([]);
  const [selectedChannelName, setSelectedChannelName] = useState<string>("");

  // State for contact administrator form modal
  const [openContactAdminModal, setOpenContactAdminModal] = useState<boolean>(false);
  const [contactFormData, setContactFormData] = useState({
    subject: '',
    message: '',
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState<boolean>(false);

  // State for channel restriction modal
  const [openChannelRestrictionModal, setOpenChannelRestrictionModal] = useState<boolean>(false);
  
  // Subscription validation hook
  const { validate, loading: validationLoading } = useSubscriptionValidation();

  const { user, markSessionExpired } = useAuth();
  const { showSuccess, showError } = useToast();
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const navigate = useNavigate();
  const observer = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  /** After first successful load, never show full-screen loader so filter toggles (e.g. Show Archived) don't reload the whole screen. */
  const hasLoadedOnceRef = useRef<boolean>(false);

  // Check if current user is channel admin for the selected channel
  const { isChannelAdmin, loading: adminLoading } = useChannelAdmin(selectedGroupId || undefined);

  // Pending anchor stored while waiting for the admin check (cache-miss path)
  const pendingMenuAnchorRef = useRef<HTMLElement | null>(null);
  // True between a three-dots click and the admin check resolving
  const [isWaitingForAdmin, setIsWaitingForAdmin] = useState(false);

  // Open the menu as soon as the admin check resolves (handles both cache-hit and cache-miss)
  useEffect(() => {
    if (isWaitingForAdmin && !adminLoading && pendingMenuAnchorRef.current) {
      setMenuAnchorEl(pendingMenuAnchorRef.current);
      pendingMenuAnchorRef.current = null;
      setIsWaitingForAdmin(false);
    }
  }, [isWaitingForAdmin, adminLoading]);

  // Socket connection and webhook listening
  useEffect(() => {
    // Connect to socket server
    const socket = socketService.connect(API_CONFIG.SOCKET_URL);
    
    if (socket) {
             // Listen for shay_realtime events (common socket communication)
       socketService.on("shay_realtime", async (eventData) => {
         try {
           // Handle different event types
           switch (eventData.type) {
             case SOCKET_KEYS.MEMBER_ADDED_TO_CHANNEL:
               await handleMemberAddedToChannel(eventData.data);
               break;
             case SOCKET_KEYS.MEMBER_REMOVED_FROM_CHANNEL:
               await handleMemberRemovedFromChannel(eventData.data);
               break;
             case SOCKET_KEYS.CHANNEL_CREATED:
               await handleChannelCreated(eventData.data);
               break;
             case SOCKET_KEYS.CHANNEL_UPDATED:
               await handleChannelUpdated(eventData.data);
               break;
             case SOCKET_KEYS.CHANNEL_DELETED:
               await handleChannelDeleted(eventData.data);
               break;
             case SOCKET_KEYS.CHANNEL_USER_UPDATE:
               await handleChannelRefresh(eventData.data);
               break;
             case SOCKET_KEYS.CHANNEL_METRICS_UPDATE:
               await handleChannelRefresh(eventData.data);
               break;
             case SOCKET_KEYS.EMAIL_DISCONNECTED:
               await handleEmailDisconnected(eventData.data);
               break;
             case SOCKET_KEYS.EMAIL_RECONNECTED:
               await handleEmailReconnected(eventData.data);
               break;
           }
         } catch (error) {
           // Handle error silently
         }
       });
     }

    // Cleanup on component unmount
    return () => {
      socketService.off("shay_realtime");
      // Note: We don't disconnect here as other components might be using the socket
    };
  }, [channels, showSuccess]);

  // Helper functions for shay_realtime events
  const handleMemberAddedToChannel = async (data: any) => {
    try {
      // Check if the logged-in user is the one who was added
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (loggedInUserData) {
        const userData = JSON.parse(loggedInUserData);
        
        // Check if the event is for the current user by comparing email_id
        if (data.emailId === userData.email_id && !channels.find(channel => channel.id === data.channelId)) {
          // Fetch the new channel details
          const newChannel = await channelService.getChannel(data.channelId);
          
          // Add the new channel to the existing list
          setChannels(prevChannels => [newChannel, ...prevChannels]);
          
          // Update total count
          setTotal(prevTotal => prevTotal + 1);
          

        }
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleMemberRemovedFromChannel = async (data: any) => {
    try {
      // Check if the logged-in user is the one who was removed
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (loggedInUserData) {
        const userData = JSON.parse(loggedInUserData);
        
        // Check if the event is for the current user by comparing email_id
        if (data.emailId === userData.email_id) {
          // Remove the channel from the list
          setChannels(prevChannels => prevChannels.filter(channel => channel.id !== data.channelId));
          
          // Update total count
          setTotal(prevTotal => Math.max(0, prevTotal - 1));
          

        }
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleChannelCreated = async (data: any) => {
    try {
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (loggedInUserData) {
        const userData = JSON.parse(loggedInUserData);
        if (data.emailId != userData.email_id) {
          return;
        }
      } else {
        return;
      }

      // Check if the channel already exists in the current list
      const channelExists = channels.find(channel => channel.id === data.channelId);
      
      if (channelExists) {
        return;
      }
      
      // Fetch the new channel details using the API
      const newChannel = await channelService.getChannel(data.channelId);
      
      if (!newChannel) {
        return;
      }
      
      // Add the new channel to the beginning of the list
      setChannels(prevChannels => [newChannel, ...prevChannels]);
      setTotal(prevTotal => prevTotal + 1);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleChannelUpdated = async (data: any) => {
    try {
      // Check if the channel exists in the current list
      const channelExists = channels.find(channel => channel.id === data.channelId);
      
      if (!channelExists) {
        return;
      }
      
      // Fetch the updated channel details using the API
      const updatedChannel = await channelService.getChannel(data.channelId);
      
      if (!updatedChannel) {
        return;
      }
      
      // Update the channel in the list
      setChannels(prevChannels => prevChannels.map(channel => 
        channel.id === updatedChannel.id ? updatedChannel : channel
      ));
    } catch (error) {
      // Handle error silently
    }
  };

  const handleChannelDeleted = async (data: any) => {
    // Check if the channel exists in the current list
    const channelExists = channels.find(channel => channel.id === data.channelId);
    
    if (channelExists) {
      // Remove the channel from the list
      setChannels(prevChannels => prevChannels.filter(channel => channel.id !== data.channelId));
      setTotal(prevTotal => Math.max(0, prevTotal - 1));
    }
  };

  const handleEmailDisconnected = async (data: any) => {
    try {
      // Find the channel that was disconnected
      const channel = channels.find(channel => channel.id === data.channelId);
      
      if (channel) {
        // Only fetch email sync status for email channels
        if (channel.channel_type !== 'email') {
          return;
        }
        
        // Update the sync status to show disconnected
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
        const response = await getEmailSyncStatus(channel.name, token, channel.channel_type);
        
        if (response.success) {
          setSyncStatus(prev => ({
            ...prev,
            [data.channelId]: response.data
          }));
        }
      }
    } catch (error) {
      console.error('Error handling email disconnected event:', error);
    }
  };

  const handleEmailReconnected = async (data: any) => {
    try {
      // Find the channel that was reconnected
      const channel = channels.find(channel => channel.id === data.channelId);
      
      if (channel) {
        // Only fetch email sync status for email channels
        if (channel.channel_type !== 'email') {
          return;
        }
        
        // Update the sync status to show reconnected
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
        const response = await getEmailSyncStatus(channel.name, token, channel.channel_type);
        
        if (response.success) {
          setSyncStatus(prev => ({
            ...prev,
            [data.channelId]: response.data
          }));
        }
        
        // Update the channel's connection status
        setChannels(prevChannels => 
          prevChannels.map(c => 
            c.id === data.channelId 
              ? { ...c, is_active: true } // Update connection status
              : c
          )
        );
      }
    } catch (error) {
      console.error('Error handling email reconnected event:', error);
    }
  };

  // Get sync status info for a channel (treats "in_progress" and "pending" as syncing, shows progress %)
  const getSyncStatusInfo = (channelId: string) => {
    const syncData = syncStatus[channelId];
    if (!syncData?.email_sync) {
      return {
        status: 'Not Connected',
        color: 'error' as 'error' | 'success' | 'warning',
        icon: <ErrorIcon />,
        progress_percentage: undefined as number | undefined
      };
    }

    const { is_active, sync_status, progress_percentage } = syncData.email_sync;
    const isSyncing = sync_status === 'pending' || sync_status === 'in_progress';

    if (!is_active) {
      return {
        status: 'Disconnected',
        color: 'error' as 'error' | 'success' | 'warning',
        icon: <ErrorIcon />,
        progress_percentage: undefined as number | undefined
      };
    }

    if (sync_status === 'success') {
      return {
        status: 'Connected',
        color: 'success' as 'error' | 'success' | 'warning',
        icon: <CheckCircleIcon />,
        progress_percentage: undefined as number | undefined
      };
    }
    if (isSyncing) {
      return {
        status: 'Syncing',
        color: 'warning' as 'error' | 'success' | 'warning',
        icon: <SyncIcon />,
        progress_percentage: typeof progress_percentage === 'number' ? progress_percentage : undefined
      };
    }
    return {
      status: 'Sync Failed',
      color: 'error' as 'error' | 'success' | 'warning',
      icon: <ErrorIcon />,
      progress_percentage: undefined as number | undefined
    };
  };

  const handleChannelRefresh = async (data: any) => {
    try {
      // Check if the channel exists in the current list
      const channelExists = channels.find(channel => channel.id === data.channelId);
      
      if (channelExists) {
        // Fetch the updated channel details using the API
        const updatedChannel = await channelService.getChannel(data.channelId);
        
        if (updatedChannel) {
          // Update the channel in the list
          setChannels(prevChannels => 
            prevChannels.map(channel => 
              channel.id === updatedChannel.id ? updatedChannel : channel
            )
          );
        }
      }
    } catch (error) {
      // Handle error silently
    }
  };

  // Fetch channels from API
  const fetchChannels = useCallback(
    async (pageNum: number, search: string = "", workspaceTag: string = "", isArchived: boolean = false, channelType: string = "all") => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: pageNum.toString(),
          size: "10",
          is_archived: isArchived.toString(),
        });

        if (search) {
          params.append("search", search);
        }

        // Only send workspace_tag if it's provided and not "all"
        if (workspaceTag && workspaceTag.trim() !== "" && workspaceTag !== "all") {
          params.append("workspace_tag", workspaceTag);
        }

        // Only send channel_type if it's provided and not "all"
        if (channelType && channelType.trim() !== "" && channelType !== "all") {
          params.append("channel_type", channelType);
        }

        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
        const response = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.LIST}?${params}`,
          {
            method: "GET",
            headers: {
              accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ChannelsResponse = await response.json();
        if (pageNum === 1) {
          setChannels(data.channels);
          // Fetch sync status for new channels (only for email channels)
          const emailChannels = data.channels.filter(c => !c.channel_type || c.channel_type === 'email');
          fetchSyncStatus(emailChannels);
        } else {
          setChannels((prev) => [...prev, ...data.channels]);
          // Fetch sync status for additional channels (only for email channels)
          const emailChannels = data.channels.filter(c => !c.channel_type || c.channel_type === 'email');
          fetchSyncStatus(emailChannels);
        }

        setTotal(data.total);
        setHasMore(data.channels.length === 10); // If we got less than 10 items, we've reached the end
        setPage(pageNum);
        setHasArchivedChannels(data.has_archived_channels ?? false);
        hasLoadedOnceRef.current = true;
      } catch (err) {
        if (err instanceof Error && err.message.includes("401")) {
          markSessionExpired();
          return;
        }
        setError(
          getFetchErrorMessage(err, "Failed to fetch channels")
        );
      } finally {
        setLoading(false);
      }
    },
    [selectedWorkspaceTag, showArchivedChannels, selectedChannelType, markSessionExpired]
  );

  // Fetch sync status for all channels (only email channels)
  const fetchSyncStatus = useCallback(async (channels: Channel[]) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const syncPromises = channels.map(async (channel) => {
        // Only fetch email sync status for email channels
        if (channel.channel_type !== 'email') {
          return { channelId: channel.id, data: null };
        }
        
        try {
          const response = await getEmailSyncStatus(channel.name, token, channel.channel_type);
          return { channelId: channel.id, data: response.data };
        } catch (error) {
          console.error(`Failed to fetch sync status for ${channel.name}:`, error);
          return { channelId: channel.id, data: null };
        }
      });

      const results = await Promise.all(syncPromises);
      const newSyncStatus: Record<string, any> = {};
      
      results.forEach(({ channelId, data }) => {
        if (data) {
          newSyncStatus[channelId] = data;
        }
      });

      setSyncStatus(prev => ({ ...prev, ...newSyncStatus }));
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  }, []);


  // Fetch agents from API
  const fetchAgents = useCallback(async () => {
    try {
      setAgentsLoading(true);
      
      const agentsData = await agentsService.getAllAgents();
      setAgents(agentsData);
    } catch (err) {
      // Do NOT call markSessionExpired() here. getAllAgents() hits ML_API_BASE_URL
      // (a separate ML service) which can return 401 for reasons unrelated to the
      // user's main session (misconfiguration, service-level auth, DEV environment
      // issues). Treating it as session expiry incorrectly logs the user out.
      console.error("Error fetching agents:", err);
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, []);
  // Initial load and search effect with proper debouncing
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchChannels(1, searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType);
      },
      searchTerm === "" ? 0 : 500
    );

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType]); // Depend on searchTerm, selectedWorkspaceTag, showArchivedChannels, and selectedChannelType


  // Fetch agents on component mount
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Infinite scroll setup
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
              if (entries[0].isIntersecting && hasMore && !loading) {
        fetchChannels(page + 1, searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType);
      }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, page, searchTerm, showArchivedChannels, selectedChannelType]
  ); // Only depend on these values

  const getChannelDescription = useCallback(
    (channel: Pick<Channel, 'description' | 'channel_description'>) => {
      return channel.channel_description ?? channel.description ?? '';
    },
    []
  );

  // Filter and search groups based on current filters
  const filteredGroups = useMemo(() => {
    return channels.filter((channel) => {
      // Search filter - include workspace_name when "all" is selected so users can search by tag
      const channelDescription = getChannelDescription(channel);
      const matchesSearch =
        channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        channelDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (selectedWorkspaceTag === "all" && channel.workspace_name && channel.workspace_name.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    });
  }, [channels, searchTerm, selectedWorkspaceTag]);

  // Handle create group button click with validation
  const handleCreateGroup = async (): Promise<void> => {
    try {
      // Validate subscription before opening modal
      const validationData = await validate({});
      
      if (validationData && !validationData.canCreateChannel) {
        // Show restriction modal if channel creation is not allowed
        setOpenChannelRestrictionModal(true);
        return;
      }
      
      // If validation passes or validationData is null (error case), proceed with opening modal
      // In case of error, we allow the user to proceed (fail open)
      setOpenAddSpaceModal(true);
    } catch (error) {
      // On error, allow user to proceed (fail open approach)
      console.error('Error validating subscription:', error);
      setOpenAddSpaceModal(true);
    }
  };

  // Handle contact administrator form
  const handleContactAdminClose = () => {
    setOpenContactAdminModal(false);
    setContactFormData({
      subject: '',
      message: '',
    });
  };

  const handleContactAdminSubmit = async () => {
    if (!contactFormData.subject.trim() || !contactFormData.message.trim()) {
      showError('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmittingContact(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.SUPPORT.CREATE_TICKET}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            subject: contactFormData.subject.trim(),
            description: contactFormData.message.trim(),
            type: 'inbox_request', // Indicate this is a request for inbox creation
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit request');
      }

      const data = await response.json();
      showSuccess('Your request has been submitted successfully. The administrator will contact you soon.');
      handleContactAdminClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  // Handle close add space modal
  const handleCloseAddSpaceModal = (): void => {
    setOpenAddSpaceModal(false);
  };

  // Handle menu open — store anchor and trigger admin check; menu opens as soon as check resolves
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    groupId: string
  ): void => {
    pendingMenuAnchorRef.current = event.currentTarget;
    setIsWaitingForAdmin(true);
    setSelectedGroupId(groupId);
  };

  // Handle menu close
  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
    setSelectedGroupId(null);
    pendingMenuAnchorRef.current = null;
    setIsWaitingForAdmin(false);
  };

  // Handle menu item click
  const handleMenuItemClick = (action: string): void => {
    if (selectedGroupId) {
      switch (action) {
        case "settings":
          const channelForSettings = channels.find(
            (c) => c.id === selectedGroupId
          );
          if (channelForSettings) {
            setSelectedChannelForSettings(channelForSettings);
            setOpenChannelSettings(true);
          }
          break;
        case "edit":
          handleEditChannel(selectedGroupId);
          break;
        case "delete":
          const channelForDelete = channels.find(
            (c) => c.id === selectedGroupId
          );
          if (channelForDelete) {
            handleDeleteConfirmOpen(channelForDelete);
          }
          break;
        case "archive":
          const channelForArchive = channels.find(
            (c) => c.id === selectedGroupId
          );
          if (channelForArchive) {
            handleArchiveConfirmOpen(channelForArchive);
          }
          break;
        case "unarchive":
          const channelForUnarchive = channels.find(
            (c) => c.id === selectedGroupId
          );
          if (channelForUnarchive) {
            handleUnarchiveChannel(channelForUnarchive);
          }
          break;
        case "disconnect":
          const channelForDisconnect = channels.find(
            (c) => c.id === selectedGroupId
          );
          if (channelForDisconnect) {
            handleDisconnectConfirmOpen(channelForDisconnect);
          }
          break;
        case "reconnect":
          const channelForReconnect = channels.find(
            (c) => c.id === selectedGroupId
          );
          if (channelForReconnect) {
            handleReconnectEmail(channelForReconnect);
          }
          break;
      }
    }
    handleMenuClose();
  };

  // Handle edit channel — both email and message channels edit through the same real Channel
  // Config tab (EmailConfigTab.tsx: its own inline "Edit Channel" dialog for email, its own
  // EditSpaceModal for message), so this always opens ChannelSettings landing on that tab with
  // its Edit dialog already open, rather than a separate top-level modal duplicating that UI.
  const handleEditChannel = (channelId: string): void => {
    const channel = channels.find((c) => c.id === channelId);
    if (!channel) return;

    setChannelSettingsInitialTabId("config");
    setChannelSettingsInitialOpenConfigEdit(true);
    setSelectedChannelForSettings(channel);
    setOpenChannelSettings(true);
  };

  // Delete confirmation dialog handlers
  const handleDeleteConfirmOpen = (channel: Channel) => {
    setChannelToDelete(channel);
    setIsDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirmClose = () => {
    setIsDeleteConfirmOpen(false);
    setChannelToDelete(null);
    setIsDeletingChannel(false);
  };

  const handleDeleteConfirm = async () => {
    if (!channelToDelete) return;

    try {
      setIsDeletingChannel(true);

      const token = localStorage.getItem('refreshToken') || '';
      
      // For email channels, delete email data using email cleanup API
      // For message/voice channels, delete the channel directly
      if (channelToDelete.channel_type === 'email') {
        // Use email cleanup API: /email/email/cleanup/{emailAddress}?archive_only=false
        const emailAddress = encodeURIComponent(channelToDelete.name);
        const response = await fetch(
          `${API_CONFIG.EMAIL_API_BASE_URL}email/cleanup/${emailAddress}?archive_only=false`,
          {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.detail || `Failed to delete email data: ${response.status}`);
        }

        // Response is successful, proceed with deletion
        // Emit socket event for channel deleted
        const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (loggedInUserData) {
          try {
            const userData = JSON.parse(loggedInUserData);
            const eventData = {
              type: SOCKET_KEYS.CHANNEL_DELETED,
              data: {
                emailId: userData.email_id,
                channelId: channelToDelete.id
              }
            };
            
            await socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }
        
        showSuccess('Email data deleted successfully');
        handleDeleteConfirmClose();
        
        // Remove the specific channel from the channels list
        setChannels(prevChannels => 
          prevChannels.filter(channel => channel.id !== channelToDelete.id)
        );
      } else {
        // For message/voice channels, delete the channel using the channel API
        const response = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.DELETE(channelToDelete.id)}`,
          {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to delete channel: ${response.status}`);
        }

        // Emit socket event for channel deleted
        const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (loggedInUserData) {
          try {
            const userData = JSON.parse(loggedInUserData);
            const eventData = {
              type: SOCKET_KEYS.CHANNEL_DELETED,
              data: {
                emailId: userData.email_id,
                channelId: channelToDelete.id
              }
            };
            
            await socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }
        
        showSuccess('Channel deleted successfully');
        handleDeleteConfirmClose();
        
        // Remove the specific channel from the channels list
        setChannels(prevChannels => 
          prevChannels.filter(channel => channel.id !== channelToDelete.id)
        );
      }
    } catch (error) {
      console.error('Failed to delete:', error);
      const errorMessage = channelToDelete.channel_type === 'email' 
        ? 'Failed to delete email data'
        : 'Failed to delete channel';
      showError(error instanceof Error ? error.message : errorMessage);
    } finally {
      setIsDeletingChannel(false);
    }
  };

  // Disconnect confirmation dialog handlers
  const handleDisconnectConfirmOpen = (channel: Channel) => {
    setChannelToDisconnect(channel);
    setIsDisconnectConfirmOpen(true);
    handleMenuClose();
  };

  const handleDisconnectConfirmClose = () => {
    setIsDisconnectConfirmOpen(false);
    setChannelToDisconnect(null);
    setIsDisconnectingChannel(false);
  };

  const handleDisconnectConfirm = async () => {
    if (!channelToDisconnect) return;

    // Only allow disconnect for email channels
    if (channelToDisconnect.channel_type !== 'email') {
      showError('Disconnect is only available for email channels');
      handleDisconnectConfirmClose();
      return;
    }

    try {
      setIsDisconnectingChannel(true);

      const token = localStorage.getItem('refreshToken') || '';
      const response = await disconnectEmailAccount(channelToDisconnect.name, token);
      
      if (response.success) {
        // Emit socket event for email disconnected
        const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (loggedInUserData) {
          try {
            const userData = JSON.parse(loggedInUserData);
            const eventData = {
              type: SOCKET_KEYS.EMAIL_DISCONNECTED,
              data: {
                emailId: userData.email_id,
                channelId: channelToDisconnect.id
              }
            };
            
            await socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }
        
        showSuccess('Email connection disconnected successfully');
        handleDisconnectConfirmClose();
        
        // Update the specific channel's connection status
        setChannels(prevChannels => 
          prevChannels.map(channel => 
            channel.id === channelToDisconnect.id 
              ? { ...channel, is_active: false } // Update connection status
              : channel
          )
        );
      } else {
        showError(response.message || 'Failed to disconnect email connection');
      }
    } catch (error) {
      console.error('Failed to disconnect email connection:', error);
      showError(error instanceof Error ? error.message : 'Failed to disconnect email connection');
    } finally {
      setIsDisconnectingChannel(false);
    }
  };

  // Handle reconnect email
  const handleReconnectEmail = async (channel: Channel) => {
    // Only allow reconnect for email channels
    if (channel.channel_type !== 'email') {
      showError('Reconnect is only available for email channels');
      return;
    }

    try {
      setIsReconnectingChannel(true);

      const token = localStorage.getItem('refreshToken') || '';
      const response = await reconnectEmailAccount(channel.name, token);
      
      if (response.success) {
        // Emit socket event for email reconnected
        const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (loggedInUserData) {
          try {
            const userData = JSON.parse(loggedInUserData);
            const eventData = {
              type: SOCKET_KEYS.EMAIL_RECONNECTED,
              data: {
                emailId: userData.email_id,
                channelId: channel.id
              }
            };
            
            await socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }
        
        showSuccess('Email connection reconnected successfully');
        
        // Update the specific channel's connection status
        setChannels(prevChannels => 
          prevChannels.map(c => 
            c.id === channel.id 
              ? { ...c, is_active: true } // Update connection status
              : c
          )
        );
        
        fetchChannels(page, searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType); // Refresh the channels list
      } else {
        showError(response.message || 'Failed to reconnect email connection');
      }
    } catch (error) {
      console.error('Failed to reconnect email connection:', error);
      showError(error instanceof Error ? error.message : 'Failed to reconnect email connection');
    } finally {
      setIsReconnectingChannel(false);
    }
  };

  // Archive confirmation dialog handlers
  const handleArchiveConfirmOpen = (channel: Channel) => {
    setChannelToArchive(channel);
    setIsArchiveConfirmOpen(true);
    handleMenuClose();
  };

  const handleArchiveConfirmClose = () => {
    setIsArchiveConfirmOpen(false);
    setChannelToArchive(null);
    setIsArchivingChannel(false);
  };

  const handleArchiveConfirm = async () => {
    if (!channelToArchive) return;

    try {
      setIsArchivingChannel(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(
          channelToArchive.id
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            is_archived: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      showSuccess("Channel archived successfully");

      // Close the confirmation dialog
      handleArchiveConfirmClose();

      // Refresh the channels list
      fetchChannels(1, searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType);
    } catch (error) {
      if (error instanceof Error && error.message.includes("401")) {
        markSessionExpired();
        return;
      }
      console.error("Failed to archive channel:", error);
      showError("Failed to archive channel. Please try again.");
    } finally {
      setIsArchivingChannel(false);
    }
  };

  const handleUnarchiveChannel = async (channel: Channel) => {
    try {
      setIsUnarchivingChannel(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(
          channel.id
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            is_archived: false,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      showSuccess("Channel unarchived successfully");

      // Refresh the channels list
      fetchChannels(1, searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType);
    } catch (error) {
      if (error instanceof Error && error.message.includes("401")) {
        markSessionExpired();
        return;
      }
      console.error("Failed to unarchive channel:", error);
      showError("Failed to unarchive channel. Please try again.");
    } finally {
      setIsUnarchivingChannel(false);
    }
  };

  // Handle view details - navigate to threads page
  const handleViewDetails = (groupId: string): void => {
    navigate(`/threads/${groupId}`);
  };

  // Handle card click - navigate to threads page
  const handleCardClick = (groupId: string): void => {
    navigate(`/threads/${groupId}`);
  };

  // Handle view all agents
  const handleViewAllAgents = (channelId: string, channelAgents: Agent[]): void => {
    const channel = channels.find((c) => c.id === channelId);
    if (channel) {
      setSelectedChannelName(channel.name);
      setSelectedChannelAgents(channelAgents);
      setOpenAgentsModal(true);
    }
  };

  // Full-screen loading only on initial load; after first load keep filters visible and show list-area loading when refetching
  if (loading && channels.length === 0 && !hasLoadedOnceRef.current) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading channels...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      backgroundColor: isDarkMode ? 'background.default' : 'background.paper',
      minHeight: '100vh'
    }}>
      {/* Search and Create Button Row - Hide when Plan Details is showing (no inboxes and no filters) */}
      {(() => {
        // Check if any filters are active
        const hasActiveFilters = 
          searchTerm !== "" || 
          showArchivedChannels || 
          selectedWorkspaceTag !== "all" || 
          selectedChannelType !== "all";
        // Show filter row when: we have channels, or filters are active, or API reported archived channels (so user can check "Show Archived Channels")
        return !(channels.length === 0 && !hasActiveFilters && !hasArchivedChannels);
      })() && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            alignItems: "center",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          <TextField
            placeholder={`Search for ${LanguageConstants.topic_s.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 180,
              flexShrink: 0,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
              "& .MuiInputBase-input": {
                color: "text.secondary",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "text.secondary",
                opacity: 0.7,
              },
            }}
            size="small"
          />
          <WorkspaceTagAutocomplete
            value={selectedWorkspaceTag}
            onChange={(value) => {
              // Store "all" if it's the all option, otherwise store the name
              const newValue = value?.id === 'all' ? 'all' : (value?.name || "all");
              setSelectedWorkspaceTag(newValue);
              setPage(1); // Reset to first page when tag changes
            }}
            label="Workspace"
            showAllOption={true}
            allOptionLabel="All Workspaces"
            size="small"
            sx={{ 
              width: 180, 
              flexShrink: 0
            }}
          />
          <FormControl size="small" sx={{ width: 150, flexShrink: 0 }}>
            <InputLabel>{LanguageConstants.topic} Type</InputLabel>
            <Select
              value={selectedChannelType}
              label={`${LanguageConstants.topic} Type`}
              onChange={(e) => {
                setSelectedChannelType(e.target.value);
                setPage(1); // Reset to first page when filter changes
              }}
            >
              <MenuItem value="all">All Types</MenuItem>
              {CHANNEL_TYPES_CONFIG.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={showArchivedChannels}
                onChange={(e) => {
                  setShowArchivedChannels(e.target.checked);
                  setPage(1); // Reset to first page when filter changes
                }}
                sx={{
                  color: 'text.secondary',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                Show Archived {LanguageConstants.topic_s}
              </Typography>
            }
          />
          <Box sx={{ flexGrow: 1 }} />
          {channelService.isUserAdmin() && (channels.length > 0 || showArchivedChannels) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateGroup}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Add {LanguageConstants.topic}
            </Button>
          )}
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Groups Grid */}
      {loading && filteredGroups.length === 0 && hasLoadedOnceRef.current && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading {LanguageConstants.topic_s.toLowerCase()}...
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
          gap: 3,
        }}
      >
        {filteredGroups.map((channel, index) => (
          <Card
            key={channel.id}
            onClick={() => handleCardClick(channel.id)}
            ref={index === filteredGroups.length - 1 ? lastElementRef : null}
                         sx={{
               position: "relative",
               transition: "all 0.2s ease-in-out",
               boxShadow: isDarkMode ? 3 : 2,
               cursor: "pointer",
               backgroundColor: isDarkMode ? 'background.paper' : 'background.paper',
               "&:hover": {
                 transform: "translateY(-2px)",
                 boxShadow: isDarkMode ? 6 : 4,
               },
               ...(!channel.is_active && {
                 opacity: 0.7,
                 backgroundColor: isDarkMode ? "grey.800" : "grey.50",
               }),
               ...(channel.is_archived && {
                 opacity: 0.6,
                 backgroundColor: isDarkMode ? "grey.900" : "grey.100",
                 border: "2px dashed",
                 borderColor: isDarkMode ? "grey.600" : "grey.400",
               }),
             }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Header with title and actions */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1.5,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, flexWrap: "wrap" }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      title={capitalizeFirstLetter(channel.name)}
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "1rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {capitalizeChannelName(channel.name)}
                    </Typography>
                    {channel.is_archived && (
                      <Chip
                        label="ARCHIVED"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          backgroundColor: isDarkMode ? "grey.700" : "grey.300",
                          color: isDarkMode ? "grey.300" : "grey.700",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      fontSize: "0.8rem",
                    }}
                  >
                   {LanguageConstants.group}:&nbsp;{capitalizeFirstLetter(channel.workspace_name)}
                  </Typography>
                </Box>

                {/* Right side: Sync Status Icon (email only), Channel Type Badge, and Actions Menu */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, ml: 1 }}>
                  {/* Sync Status Icon - Only for email channels */}
                  {(() => {
                    const channelType = channel.channel_type || 'email';
                    if (channelType === 'email' && syncStatus[channel.id]) {
                      const statusInfo = getSyncStatusInfo(channel.id);
                      const isSyncing = statusInfo.status === 'Syncing';

                      return (
                        <Tooltip title={statusInfo.status}>
                          {isSyncing ? (
                            <Chip
                              icon={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 16,
                                    height: 16,
                                    animation: 'spin 1s linear infinite',
                                    '@keyframes spin': {
                                      '0%': { transform: 'rotate(0deg)' },
                                      '100%': { transform: 'rotate(360deg)' }
                                    }
                                  }}
                                >
                                  <SyncIcon sx={{ fontSize: 16 }} />
                                </Box>
                              }
                              label="Syncing"
                              color="warning"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                                mr: 1.25,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                backgroundColor: (() => {
                                  switch (statusInfo.color) {
                                    case 'success': return '#4caf50'; // Green
                                    case 'warning': return '#ff9800'; // Orange
                                    case 'error': return '#f44336'; // Red
                                    default: return '#9e9e9e'; // Grey
                                  }
                                })(),
                                color: 'white',
                                mr: 1.25,
                              }}
                            >
                              {React.cloneElement(statusInfo.icon, {
                                sx: { fontSize: 12, color: 'white' }
                              })}
                            </Box>
                          )}
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}
                  {/* Channel type chip with provider-specific icon (Gmail/Outlook, Slack/WhatsApp/etc.) */}
                  {(() => {
                    const channelType = channel.channel_type || 'email';
                    if (!getChannelTypeById(channelType)) return null;
                    // Use provider (top-level or channel_settings) for chip icon (email + message)
                    const provider =
                      channel.provider ??
                      (channel.channel_settings as { provider?: string } | undefined)?.provider;
                    return (
                      <ChannelTypeChip
                        channelType={channelType}
                        emailProvider={provider}
                        messagingProvider={provider}
                      />
                    );
                  })()}
                  {/* Actions Menu */}
                  <IconButton
                    size="small"
                    onMouseEnter={() => prefetchChannelAdmin(channel.id)}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click when menu is clicked
                      handleMenuOpen(e, channel.id);
                    }}
                    sx={{
                      color: "text.secondary",
                      "&:hover": { color: "primary.main" },
                      zIndex: 1,
                      position: "relative",
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </Box>

              {/* New Grid Layout with Boxes */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                  }}
                >
                                     {/* Users & Workflows Box */}
                   <Paper
                     sx={{
                       p: 1.5,
                       backgroundColor: isDarkMode ? "primary.900" : "primary.50",
                       border: "1px solid",
                       borderColor: isDarkMode ? "grey.700" : "grey.200",
                       boxShadow: "none",
                       borderRadius: 1.5,
                     }}
                   >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                        <People sx={{ fontSize: 18, color: "primary.main" }} />
                                                <Typography
                           variant="h6"
                           fontWeight={700}
                           sx={{ fontSize: "0.8rem" }}
                         >
                           {channel.total_users || 0}
                         </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.primary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Users
                        </Typography>
                      </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                         <FlashIcon sx={{ 
                           fontSize: 18, 
                           color: (channel.workflow_count || 0) === 0 ? "text.disabled" : "primary.main" 
                         }} />
                                                 <Typography
                           variant="h6"
                           fontWeight={700}
                           sx={{ 
                             fontSize: "0.8rem",
                             color: (channel.workflow_count || 0) === 0 ? "text.disabled" : "text.primary"
                           }}
                         >
                           {channel.workflow_count || 0}
                         </Typography>
                         <Typography
                           variant="body2"
                           fontWeight={600}
                           sx={{ 
                             fontSize: "0.8rem",
                             color: (channel.workflow_count || 0) === 0 ? "text.disabled" : "text.primary"
                           }}
                         >
                           Workflows
                         </Typography>
                       </Box>
                   </Paper>

                   {/* {LanguageConstants.datasource_s} Box */}
                   <Paper
                     sx={{
                       p: 1.5,
                       backgroundColor: isDarkMode ? "info.900" : "info.50",
                       border: "1px solid",
                       borderColor: isDarkMode ? "grey.700" : "grey.200",
                       boxShadow: "none",
                       borderRadius: 1.5,
                     }}
                   >
                                          <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <Storage sx={{ fontSize: 18, color: "info.main" }} />
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {channel.total_datasources || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.primary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {LanguageConstants.datasource_s}
                        </Typography>
                      </Box>
                     <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                        minHeight: 20,
                        alignItems: "center",
                      }}
                    >
                      {channel.total_datasources > 0 && channel.datasource && channel.datasource.length > 0 ? (
                        <>
                          {/* Show up to 3 {LanguageConstants.datasource.toLowerCase()} icons */}
                          {channel.datasource.slice(0, 3).map((ds, index) => (
                            <Tooltip
                              key={index}
                              title={ds.datasource_name || `${LanguageConstants.datasource} ${index + 1}`}
                              placement="top"
                              arrow
                            >
                              <Box>
                                {ds.file_type ? (
                                  (() => {
                                    const fileType = ds.file_type.toLowerCase();
                                    
                                    if (fileType.includes('/')) {
                                      const parts = fileType.split('/');
                                      if (parts.length === 2) {
                                        const type = parts[0];
                                        const subtype = parts[1];
                                        
                                        if (type === "text") {
                                          return <Description sx={{ fontSize: 16, color: "#2196f3" }} />;
                                        }
                                        if (type === "image") {
                                          return <Image sx={{ fontSize: 16, color: "#4caf50" }} />;
                                        }
                                        if (type === "application") {
                                          if (subtype === "pdf") {
                                            return <PictureAsPdf sx={{ fontSize: 16, color: "#f44336" }} />;
                                          }
                                          if (["msword", "vnd.openxmlformats-officedocument.wordprocessingml.document", "vnd.oasis.opendocument.text"].includes(subtype)) {
                                            return <Description sx={{ fontSize: 16, color: "#2196f3" }} />;
                                          }
                                          if (["vnd.ms-excel", "vnd.openxmlformats-officedocument.spreadsheetml.sheet", "vnd.oasis.opendocument.spreadsheet"].includes(subtype)) {
                                            return <TableChart sx={{ fontSize: 16, color: "#ff9800" }} />;
                                          }
                                          if (["vnd.ms-powerpoint", "vnd.openxmlformats-officedocument.presentationml.presentation", "vnd.oasis.opendocument.presentation"].includes(subtype)) {
                                            return <Slideshow sx={{ fontSize: 16, color: "#e91e63" }} />;
                                          }
                                          if (["zip", "x-rar-compressed", "x-7z-compressed", "x-tar", "gzip"].includes(subtype)) {
                                            return <FolderZip sx={{ fontSize: 16, color: "#795548" }} />;
                                          }
                                          if (["javascript", "typescript", "json", "xml", "python", "java", "cpp", "c", "php"].includes(subtype)) {
                                            return <Code sx={{ fontSize: 16, color: "#607d8b" }} />;
                                          }
                                          if (subtype === "octet-stream") {
                                            return <Storage sx={{ fontSize: 16, color: "#9c27b0" }} />;
                                          }
                                        }
                                      }
                                    }
                                    
                                    if (fileType === "pdf") {
                                      return <PictureAsPdf sx={{ fontSize: 16, color: "#f44336" }} />;
                                    }
                                    
                                    return <Language sx={{ fontSize: 16, color: "text.secondary" }} />;
                                  })()
                                ) : (
                                  <Language sx={{ fontSize: 16, color: "text.secondary" }} />
                                )}
                              </Box>
                            </Tooltip>
                          ))}
                                                      {/* Show "more" count if there are more than 3 */}
                           {channel.total_datasources > 3 && (
                             <Typography
                               variant="body2"
                               color="text.secondary"
                               sx={{ fontSize: "0.7rem" }}
                             >
                               +{channel.total_datasources - 3} more
                             </Typography>
                           )}
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          sx={{ fontSize: "0.75rem", fontStyle: "italic" }}
                        >
                          No Data
                        </Typography>
                      )}
                    </Box>
                   </Paper>

                                      {/* Apps Box - Moved to left position */}
                  <Paper
                    sx={{
                      p: 1.5,
                      backgroundColor: isDarkMode ? "success.900" : "success.50",
                      border: "1px solid",
                      borderColor: isDarkMode ? "grey.700" : "grey.200",
                      boxShadow: "none",
                      borderRadius: 1.5,
                    }}
                  >
                                         <Box
                       sx={{
                         display: "flex",
                         alignItems: "center",
                         gap: 0.5,
                         mb: 0.5,
                       }}
                     >
                       <Apps sx={{ fontSize: 18, color: "success.main" }} />
                                               <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {channel.total_apps_connected || 0}
                        </Typography>
                       <Typography
                         variant="body2"
                         fontWeight={600}
                         color="text.primary"
                         sx={{ fontSize: "0.8rem" }}
                       >
                         Apps
                       </Typography>
                     </Box>
                   <Box
                     sx={{
                       display: "flex",
                       gap: 0.5,
                       flexWrap: "wrap",
                       minHeight: 20,
                       alignItems: "center",
                     }}
                   >
                     {channel.total_apps_connected > 0 && channel.app && channel.app.length > 0 ? (
                       <>
                         {/* Show up to 3 app icons */}
                         {channel.app.slice(0, 3).map((app: { app_name: string; icon_url: string; app_key?: string }, index: number) => {
                           const isOdoo = app.app_key?.startsWith('odoo') || app.app_name?.toLowerCase().includes('odoo');
                           // Use the app-specific icon key so different Odoo products (CRM, Helpdesk, Inventory…)
                           // each resolve to their own image; normalise the bare 'odoo' key to 'odoo_crm'.
                           const odooIconKey = app.app_key === 'odoo' ? 'odoo_crm' : (app.app_key || 'odoo_crm');
                           const effectiveIconUrl = app.icon_url || (isOdoo ? `/images/apps/${odooIconKey}.png` : '');
                           return (
                             <Tooltip
                               key={index}
                               title={app.app_name || `App ${index + 1}`}
                               placement="top"
                               arrow
                             >
                               <Box>
                                 {effectiveIconUrl ? (
                                   <img
                                     src={effectiveIconUrl}
                                     alt={app.app_name || "App"}
                                     style={{
                                       width: 20,
                                       height: 20,
                                       objectFit: "contain",
                                     }}
                                     onError={(e) => {
                                       const img = e.target as HTMLImageElement;
                                       if (img.dataset.odooFallback !== 'true') {
                                         img.dataset.odooFallback = 'true';
                                         img.src = '/images/apps/odoo_crm.png';
                                       }
                                     }}
                                   />
                                 ) : (
                                   <Apps sx={{ fontSize: 18, color: "success.main" }} />
                                 )}
                               </Box>
                             </Tooltip>
                           );
                         })}
                                                    {/* Show "more" count if there are more than 3 */}
                         {channel.total_apps_connected > 3 && (
                           <Typography
                             variant="body2"
                             color="text.secondary"
                             sx={{ fontSize: "0.7rem" }}
                           >
                             +{channel.total_apps_connected - 3} more
                           </Typography>
                         )}
                       </>
                     ) : (
                       <Typography
                         variant="body2"
                         color="text.disabled"
                         sx={{ fontSize: "0.75rem", fontStyle: "italic" }}
                       >
                         No Data
                       </Typography>
                     )}
                   </Box>
                  </Paper>

                  {/* Agents Box - Moved to right position and reduced size */}
                  <Paper
                    sx={{
                      p: 1.5,
                      backgroundColor: isDarkMode ? "primary.900" : "primary.50",
                      border: "1px solid",
                      borderColor: isDarkMode ? "grey.700" : "grey.200",
                      boxShadow: "none",
                      borderRadius: 1.5,
                    }}
                  >
                                     <Box
                     sx={{
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "space-between",
                       gap: 0.5,
                       mb: 0.5,
                     }}
                   >
                     <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                       <SmartToyIcon sx={{ fontSize: 18, color: "primary.main" }} />
                                           <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {agentsLoading ? (
                          <CircularProgress size={16} sx={{ color: "primary.main" }} />
                        ) : (
                          agents.length || 0
                        )}
                      </Typography>
                     <Typography
                       variant="body2"
                       fontWeight={600}
                       color="text.primary"
                       sx={{ fontSize: "0.8rem" }}
                     >
                       Agents
                     </Typography>
                     </Box>
                     {/* View All button on the same line as title */}
                     {!agentsLoading && agents.length > 2 && (
                       <Button
                         size="small"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleViewAllAgents(channel.id, agents);
                         }}
                         sx={{
                           p: 0.25,
                           minWidth: "auto",
                           color: "primary.main",
                           fontSize: "0.7rem",
                           textTransform: "none",
                           "&:hover": {
                             backgroundColor: "rgba(25, 118, 210, 0.1)",
                           },
                         }}
                       >
                         View All
                       </Button>
                     )}
                   </Box>
                                     <Box
                     sx={{
                       display: "flex",
                       gap: 0.5,
                       flexWrap: "wrap",
                       minHeight: 20,
                       alignItems: "center",
                     }}
                   >
                                         {agentsLoading ? (
                       <Typography
                         variant="body2"
                         color="text.disabled"
                         sx={{ fontSize: "0.75rem", fontStyle: "italic" }}
                       >
                         Loading...
                       </Typography>
                     ) : agents.length > 0 ? (
                       <>
                         {/* Show up to 2 agent avatars */}
                         {agents.slice(0, 2).map((agent, index) => (
                           <Tooltip
                             key={agent.agent_id}
                             title={agent.name
                               .split('_')
                               .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                               .map(word => word.replace('Agent', ''))
                               .join(' ')}
                             placement="top"
                             arrow
                           >
                             <Avatar
                               sx={{
                                 width: 20,
                                 height: 20,
                                 fontSize: "0.6rem",
                                 bgcolor: "primary.main",
                                 color: "white",
                                 cursor: "pointer",
                               }}
                               onClick={(e) => {
                                 e.stopPropagation();
                                 handleViewAllAgents(channel.id, agents);
                               }}
                             >
                               {capitalizeFirstLetter(agent.name).charAt(0)}
                             </Avatar>
                           </Tooltip>
                         ))}
                                                   {/* Show "more" count closer to avatars if there are more than 2 */}
                          {agents.length > 2 && (
                            <Typography
                               variant="body2"
                               color="text.secondary"
                               sx={{ fontSize: "0.7rem" }}
                             >
                               +{agents.length - 2} more
                             </Typography>
                          )}
                       </>
                     ) : (
                       <Typography
                         variant="body2"
                         color="text.disabled"
                         sx={{ fontSize: "0.75rem", fontStyle: "italic" }}
                       >
                         No Agents
                       </Typography>
                     )}
                  </Box>
                </Paper>
                </Box>
              </Box>

              {/* Footer with unread conversations and arrow - Fixed position from bottom */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  position: "absolute",
                  bottom: 12,
                  left: 16,
                  right: 16,
                }}
              >
                <Box sx={{ visibility: "hidden", display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ChatBubbleOutline sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    0 Unread Conversations
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click when arrow is clicked
                    handleViewDetails(channel.id);
                  }}
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      backgroundColor: "transparent", // Override default Material-UI hover background
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="primary.main"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    View {LanguageConstants.topic} →
                  </Typography>
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Loading indicator for infinite scroll */}
      {loading && channels.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Menu for group actions */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {isChannelAdmin && (
          <MenuItem onClick={() => handleMenuItemClick("edit")}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {isChannelAdmin && (() => {
          const selectedChannel = channels.find((c) => c.id === selectedGroupId);
          const isEmailChannel = selectedChannel?.channel_type === 'email';
          // Use sync status (source of red/green dot) when available, else channel.is_active
          const syncIsActive = selectedGroupId ? syncStatus[selectedGroupId]?.email_sync?.is_active : undefined;
          const isDisconnected = selectedChannel && (syncIsActive === false || selectedChannel.is_active === false);
          
          return isEmailChannel && isDisconnected ? (
            <MenuItem onClick={() => handleMenuItemClick("reconnect")}>
              <ListItemIcon>
                <Refresh fontSize="small" />
              </ListItemIcon>
              <ListItemText>Reconnect</ListItemText>
            </MenuItem>
          ) : isEmailChannel ? (
            <MenuItem onClick={() => handleMenuItemClick("disconnect")}>
              <ListItemIcon>
                <LinkOff fontSize="small" />
              </ListItemIcon>
              <ListItemText>Disconnect</ListItemText>
            </MenuItem>
          ) : null;
        })()}
        {isChannelAdmin && (
          <MenuItem onClick={() => handleMenuItemClick("delete")}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}

        {isChannelAdmin && selectedGroupId && (
          (() => {
            const selectedChannel = channels.find((c) => c.id === selectedGroupId);
            return selectedChannel?.is_archived ? (
              <MenuItem onClick={() => handleMenuItemClick("unarchive")}>
                <ListItemIcon>
                  <Unarchive fontSize="small" />
                </ListItemIcon>
                <ListItemText>Unarchive</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={() => handleMenuItemClick("archive")}>
                <ListItemIcon>
                  <Archive fontSize="small" />
                </ListItemIcon>
                <ListItemText>Archive</ListItemText>
              </MenuItem>
            );
          })()
        )}

        <MenuItem onClick={() => handleMenuItemClick("settings")}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
      </Menu>

              {/* Add {LanguageConstants.topic} Modal */}
      {/* Channel Restriction Modal */}
      <ChannelRestrictionModal
        open={openChannelRestrictionModal}
        onClose={() => setOpenChannelRestrictionModal(false)}
        restrictionType="channel"
      />

      <AddSpaceModal
        open={openAddSpaceModal}
        onClose={handleCloseAddSpaceModal}
        onSpaceCreated={() => {
          // Refresh the channels list after creating a new space
          fetchChannels(1, searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType);
                            showSuccess(`${LanguageConstants.topic} created successfully`);
        }}
      />

      {/* Channel Settings */}
      <ChannelSettings
        open={openChannelSettings}
        onClose={() => {
          setOpenChannelSettings(false);
          setSelectedChannelForSettings(null);
          setChannelSettingsInitialTabId(undefined);
          setChannelSettingsInitialOpenConfigEdit(false);
        }}
        initialTabId={channelSettingsInitialTabId}
        initialOpenConfigEdit={channelSettingsInitialOpenConfigEdit}
        // PR #639 review — the standalone EditSpaceModal this "Edit" menu item used to open had
        // its own onSpaceUpdated callback (refresh + success toast); routing "Edit" through this
        // same ChannelSettings drawer instead (see handleEditChannel) dropped that refresh since
        // onChannelUpdated was never wired here. Mirrors that removed callback exactly.
        onChannelUpdated={() => {
          fetchChannels(1, searchTerm, selectedWorkspaceTag, showArchivedChannels, selectedChannelType);
          showSuccess(`${LanguageConstants.topic} updated successfully`);
        }}
        channelId={selectedChannelForSettings?.id}
        channelName={selectedChannelForSettings?.name}
        channelDescription={selectedChannelForSettings ? getChannelDescription(selectedChannelForSettings) : undefined}
        channelTags={selectedChannelForSettings?.channel_tags}
        channelSubtags={selectedChannelForSettings?.channel_sub_tags}
        channelBusinessRules={selectedChannelForSettings?.channel_business_rules}
        isChannelArchived={selectedChannelForSettings?.is_archived || false}
        channelType={selectedChannelForSettings?.channel_type}
        workspaceTag={selectedChannelForSettings?.workspace_name}
        workspaceId={selectedChannelForSettings?.workspace_id}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return; // Prevent closing when clicking outside
          }
          handleDeleteConfirmClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          {channelToDelete?.channel_type === 'email' ? 'Delete Email Data' : 'Delete Channel'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              {channelToDelete?.channel_type === 'email' 
                ? `Are you sure you want to delete all email data for "${channelToDelete?.name}"?`
                : `Are you sure you want to delete the channel "${channelToDelete?.name}"?`
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {channelToDelete?.channel_type === 'email'
                ? 'This action will permanently remove all emails, threads, and associated data. This cannot be undone.'
                : 'This action will permanently delete the channel and all associated data. This cannot be undone.'
              }
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteConfirmClose}
            disabled={isDeletingChannel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeletingChannel}
          >
            {isDeletingChannel ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={isDisconnectConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return; // Prevent closing when clicking outside
          }
          handleDisconnectConfirmClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            // Match app dark/light theme (was hard-coded white → invisible light text in dark mode)
            backgroundColor: muiTheme.palette.background.paper,
            backgroundImage: 'none',
            color: muiTheme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>
          Disconnect Email Connection
        </DialogTitle>
        <DialogContent sx={{ color: 'text.secondary' }}>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ color: 'text.primary' }}>
              Are you sure you want to disconnect the email connection for "{channelToDisconnect?.name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action will stop email synchronization and may affect email functionality.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDisconnectConfirmClose}
            disabled={isDisconnectingChannel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisconnectConfirm}
            variant="contained"
            color="error"
            disabled={isDisconnectingChannel}
          >
            {isDisconnectingChannel ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Disconnecting...
              </>
            ) : (
              'Disconnect'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={isArchiveConfirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return; // Prevent closing when clicking outside
          }
          handleArchiveConfirmClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Archive {LanguageConstants.topic}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to archive the {LanguageConstants.topic.toLowerCase()} "{channelToArchive?.name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Archived {LanguageConstants.topic_s.toLowerCase()} will be hidden from the main view but can be restored later.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleArchiveConfirmClose}
            disabled={isArchivingChannel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            variant="contained"
            color="warning"
            disabled={isArchivingChannel}
          >
            {isArchivingChannel ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Archiving...
              </>
            ) : (
              `Archive ${LanguageConstants.topic}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agents Modal */}
      <Dialog
        open={openAgentsModal}
        onClose={() => setOpenAgentsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6">
              Agents in {selectedChannelName}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {selectedChannelAgents.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: 2,
                }}
              >
                {selectedChannelAgents.map((agent) => (
                  <Paper
                    key={agent.agent_id}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "grey.200",
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.9rem",
                          bgcolor: "primary.main",
                          color: "white",
                        }}
                      >
                        {capitalizeFirstLetter(agent.name).charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          color="text.primary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '200px'
                          }}
                          title={agent.name
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')}
                        >
                          {agent.name
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')}
                        </Typography>
                        {/* <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Agent ID: {agent.agent_id}
                        </Typography> */}
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.8rem", lineHeight: 1.4 }}
                    >
                      {agent.description || "No description available"}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <SmartToyIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Agents Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This {LanguageConstants.topic.toLowerCase()} doesn't have any agents configured yet.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAgentsModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Empty State */}
      {filteredGroups.length === 0 && !loading && (
        <>
          {/* Show Plan Details and Create Message only when no channels exist AND no filters are active */}
          {(() => {
            // Check if any filters are active
            const hasActiveFilters = 
              searchTerm !== "" || 
              showArchivedChannels || 
              selectedWorkspaceTag !== "all" || 
              selectedChannelType !== "all";
            
            // Show plan details only when no channels exist AND no filters are active
            return channels.length === 0 && !hasActiveFilters;
          })() ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 0,
                mt: 0,
                alignItems: 'flex-start',
              }}
            >
              {/* Left Column - Plan Details (hidden when subscription is disabled) */}
              {!DISABLE_SUBSCRIPTION && (
                <>
                  <Box sx={{ flex: 1, pr: { xs: 0, md: 4 } }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 2,
                        color: 'text.primary',
                      }}
                    >
                      Current Plan Details
                    </Typography>
                    <PlanDetails showDetailedInfo={true} showCardBorder={false} />
                  </Box>
                  {/* Vertical Divider */}
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      width: '1px',
                      backgroundColor: 'divider',
                      mx: 4,
                      alignSelf: 'stretch',
                      minHeight: '400px',
                    }}
                  />
                </>
              )}
              {/* Right Column - Create First Inbox Message / No channels state */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  pl: { xs: 0, md: DISABLE_SUBSCRIPTION ? 0 : 4 },
                  pt: { xs: 4, md: 0 },
                }}
              >
                {channelService.isUserAdmin() ? (
                  <>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2,
                        backgroundColor: isDarkMode 
                          ? 'rgba(25, 118, 210, 0.12)' 
                          : 'rgba(25, 118, 210, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        alignSelf: 'center',
                      }}
                    >
                      <NotificationsActive 
                        sx={{ 
                          fontSize: 40, 
                          color: 'primary.main',
                        }} 
                      />
                    </Box>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 2,
                        color: 'text.primary',
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      NO CHANNELS FOUND
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 4,
                        lineHeight: 1.7,
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      Get started by creating your first channel.
                    </Typography>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreateGroup}
                        disabled={validationLoading}
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Add {LanguageConstants.topic}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box
                      id="contact-admin-icon-container"
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2,
                        backgroundColor: isDarkMode 
                          ? 'rgba(255, 152, 0, 0.12)' 
                          : 'rgba(255, 152, 0, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        alignSelf: 'center',
                      }}
                    >
                      <ContactSupport 
                        id="contact-admin-icon"
                        sx={{ 
                          fontSize: 40, 
                          color: 'warning.main',
                        }} 
                      />
                    </Box>
                    <Box
                      id="contact-admin-content-container"
                      sx={{
                        mb: 3,
                        width: '100%',
                      }}
                    >
                      <Typography 
                        id="contact-admin-heading"
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 1.5,
                          color: 'text.primary',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <Info id="contact-admin-info-icon" sx={{ fontSize: 24, color: 'warning.main' }} />
                        Contact administrator to get started
                      </Typography>
                      <Typography 
                        id="contact-admin-description"
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ 
                          lineHeight: 1.7,
                          textAlign: 'center',
                          mb: 3,
                        }}
                      >
                        To get started, please contact your administrator to create your first inbox.
                      </Typography>
                      {/* <Box id="contact-admin-button-container" sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Button
                          id="contact-admin-button"
                          variant="contained"
                          startIcon={<ContactSupport />}
                          onClick={() => setOpenContactAdminModal(true)}
                          sx={{
                            backgroundColor: "warning.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "warning.dark",
                            },
                            px: 3,
                            py: 1.5,
                          }}
                        >
                          Contact Administrator
                        </Button>
                      </Box> */}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          ) : (
            /* Show search/filter empty state when channels exist but are filtered out, or when filters return no results */
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                color: "text.secondary",
              }}
            >
              <NotificationsActive sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No {LanguageConstants.topic_s.toLowerCase()} found
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {(() => {
                  // Determine message based on active filters
                  const activeFilters: string[] = [];
                  if (searchTerm && searchTerm.trim() !== "") activeFilters.push("search");
                  if (showArchivedChannels) activeFilters.push("archived");
                  if (selectedWorkspaceTag !== "all") activeFilters.push("workspace");
                  if (selectedChannelType !== "all") activeFilters.push("channel type");

                  if (activeFilters.length === 0) {
                    // This case should rarely happen since we check hasActiveFilters above
                    // But handle it for safety
                    return `Create your first ${LanguageConstants.topic.toLowerCase()} to get started`;
                  } else if (activeFilters.length === 1) {
                    if (searchTerm && searchTerm.trim() !== "") {
                      return `No ${LanguageConstants.topic_s.toLowerCase()} match your search "${searchTerm}"`;
                    } else if (showArchivedChannels) {
                      return "No archived channels found";
                    } else if (selectedWorkspaceTag !== "all") {
                      return `No channels found for this workspace`;
                    } else if (selectedChannelType !== "all") {
                      const channelTypeLabel = CHANNEL_TYPES_CONFIG.find(type => type.id === selectedChannelType)?.label || selectedChannelType;
                      return `No ${channelTypeLabel.toLowerCase()} channels found`;
                    }
                  } else {
                    // Multiple filters active
                    const filterNames: string[] = [];
                    if (searchTerm && searchTerm.trim() !== "") filterNames.push(`search "${searchTerm}"`);
                    if (showArchivedChannels) filterNames.push("archived filter");
                    if (selectedWorkspaceTag !== "all") filterNames.push("workspace filter");
                    if (selectedChannelType !== "all") {
                      const channelTypeLabel = CHANNEL_TYPES_CONFIG.find(type => type.id === selectedChannelType)?.label || selectedChannelType;
                      filterNames.push(`${channelTypeLabel.toLowerCase()} filter`);
                    }
                    return `No ${LanguageConstants.topic_s.toLowerCase()} match your ${filterNames.join(" and ")}`;
                  }
                  return `Try adjusting your filters or search`;
                })()}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Contact Administrator Modal */}
      <Dialog
        id="contact-admin-modal"
        open={openContactAdminModal}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            handleContactAdminClose();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          id="contact-admin-modal-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography id="contact-admin-modal-title-text" variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Contact Administrator
          </Typography>
          <IconButton
            id="contact-admin-modal-close-btn"
            onClick={handleContactAdminClose}
            sx={{
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
              },
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent id="contact-admin-modal-content" sx={{ pt: 2 }}>
          <Typography id="contact-admin-modal-description" variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide details about your request to create an inbox. The administrator will review and get back to you.
          </Typography>

          <TextField
            id="contact-admin-subject-input"
            fullWidth
            label="Subject"
            value={contactFormData.subject}
            onChange={(e) =>
              setContactFormData({ ...contactFormData, subject: e.target.value })
            }
            placeholder="e.g., Request to create inbox for support team"
            required
            margin="normal"
            sx={{ mb: 2 }}
          />

          <TextField
            id="contact-admin-message-input"
            fullWidth
            label="Message"
            value={contactFormData.message}
            onChange={(e) =>
              setContactFormData({ ...contactFormData, message: e.target.value })
            }
            placeholder="Please describe your requirements..."
            required
            multiline
            rows={6}
            margin="normal"
          />
        </DialogContent>

        <DialogActions id="contact-admin-modal-actions" sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            id="contact-admin-modal-cancel-btn"
            onClick={handleContactAdminClose}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              borderColor: muiTheme.palette.divider,
              color: muiTheme.palette.text.secondary,
              '&:hover': {
                borderColor: muiTheme.palette.text.primary,
                color: muiTheme.palette.text.primary,
                backgroundColor: muiTheme.palette.action.hover,
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            id="contact-admin-modal-submit-btn"
            variant="contained"
            onClick={handleContactAdminSubmit}
            disabled={
              !contactFormData.subject.trim() ||
              !contactFormData.message.trim() ||
              isSubmittingContact
            }
            startIcon={
              isSubmittingContact ? (
                <CircularProgress id="contact-admin-submit-loading" size={20} color="inherit" />
              ) : (
                <ContactSupport />
              )
            }
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              backgroundColor: 'warning.main',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            {isSubmittingContact ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupsPage;
